import { FC, PropsWithChildren, ReactNode, SyntheticEvent, forwardRef, useCallback, useState } from 'react';

import { useLinkComponent } from '@eneco/link';

import {
  leftIconWrapperBaseClass,
  leftIconWrapperClass,
  notificationBoxBaseClass,
  notificationBoxClass,
  notificationBoxElevatedClass,
  notificationBoxVariantClass,
  notificationCloseButtonClass,
} from './NotificationBox.css';
import { ChatIcon, CloseIcon, ErrorIcon, InfoIcon, SuccessIcon, WarningIcon } from '../../icons';
import { useI18nTranslation } from '../../providers/i18n';
import { getClassNames } from '../../util/css';
import { Box } from '../Box/Box';
import { Heading } from '../Heading/Heading';
import { IconButton } from '../IconButton/IconButton';
import { Stack } from '../Stack/Stack';
import { Text } from '../Text/Text';
import { TextLink } from '../TextLink/TextLink';

type ResponsiveKey = 'initial' | 'sm' | 'md' | 'lg' | 'xl';
type ResponsiveValue<T> = T | Partial<Record<ResponsiveKey, T>>;
type EmphasisValue = 'high' | 'low';

const iconMapper: Record<VariantTypes, ReactNode> = {
  info: <InfoIcon />,
  success: <SuccessIcon />,
  warning: <WarningIcon />,
  error: <ErrorIcon />,
  chat: <ChatIcon />,
};

const ownClassName = 'notification-box-sparky';

function filterEmphasisObject(emphasis: EmphasisSparky) {
  const forcedEmphasisObject: EmphasisSparky = { initial: 'low' };

  for (const [mq, value] of Object.entries(emphasis)) {
    if (value === 'high' && (mq === 'lg' || mq === 'xl')) {
      forcedEmphasisObject[mq as keyof EmphasisSparky] = value;
      break;
    }
  }

  return forcedEmphasisObject;
}

type CanCloseProps = { closeable?: boolean; onClose?: (event?: SyntheticEvent) => void };

type InnerProps = {
  variant?: VariantTypes;
  title?: string;
  headingLevel?: 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  text: ReactNode;
  link?: { label?: string | null; href?: string | null };
  emphasis?: Exclude<NotificationVariants['emphasis'], undefined>;
  icon?: ReactNode;
  closeable?: boolean;
};

export type NotificationProps = Omit<NotificationVariants, 'variant'> & { isAlert: boolean } & InnerProps &
  CanCloseProps;

export const InnerNotificationBox: FC<InnerProps> = ({
  title,
  headingLevel = 'h4',
  text,
  link,
  variant = 'info',
  emphasis = 'low',
  icon,
  closeable,
}) => {
  const iconClassName = getClassNames(leftIconWrapperBaseClass, leftIconWrapperClass[variant]);
  const Link = useLinkComponent();
  return (
    <Stack direction="row" gap={emphasis === 'high' ? '3' : '2'}>
      <Stack.Item grow={false}>
        <div className={iconClassName}>{icon ?? iconMapper[variant]}</div>
      </Stack.Item>
      <Stack>
        {title && (
          <Box paddingRight={closeable ? 6 : 0}>
            <Heading as={headingLevel} size={emphasis === 'high' ? 'XS' : '3XS'} color="inherit">
              {title}
            </Heading>
          </Box>
        )}
        <Box paddingTop="1">
          <Text as={typeof text === 'string' ? 'span' : 'div'} size={emphasis === 'high' ? 'BodyM' : 'BodyS'}>
            {text}
          </Text>
        </Box>
        {link?.href ? (
          <Box paddingTop="1">
            <Link href={link.href} linkType="external">
              <TextLink emphasis="high" type="button">
                {link?.label}
              </TextLink>
            </Link>
          </Box>
        ) : null}
      </Stack>
    </Stack>
  );
};

export const NotificationBox = forwardRef<HTMLDivElement, PropsWithChildren<NotificationProps>>(
  (
    {
      isAlert,
      variant = 'info',
      emphasis = 'low',
      title,
      headingLevel,
      text,
      link,
      isElevated,
      closeable,
      onClose,
      icon,
    },
    ref,
  ) => {
    const closeText = useI18nTranslation('close');

    const getForcedEmphasis = () => {
      if (typeof emphasis !== 'string') return filterEmphasisObject(emphasis);
      if (emphasis === 'high') return { initial: 'low' as const, lg: emphasis };

      return 'low' as const;
    };

    const forcedEmphasis = getForcedEmphasis();

    const [hasHandledClose, setHasHandledClose] = useState(false);

    const onCloseHandler = useCallback(
      (event: SyntheticEvent<HTMLButtonElement | HTMLAnchorElement>) => {
        onClose?.(event);
        setHasHandledClose(true);
      },
      [onClose],
    );

    if (hasHandledClose) {
      return null;
    }

    return (
      <div
        ref={ref}
        className={getClassNames(
          ownClassName,
          notificationBoxBaseClass,
          notificationBoxClass({ emphasis: forcedEmphasis }),
          notificationBoxVariantClass[variant],
          {
            [notificationBoxElevatedClass]: !!isElevated,
          },
        )}
        role={isAlert ? 'alert' : undefined}>
        <InnerNotificationBox
          variant={variant}
          title={title}
          headingLevel={headingLevel}
          text={text}
          link={link}
          emphasis={forcedEmphasis}
          icon={icon}
          closeable={closeable}
        />
        {closeable && (
          <div className={notificationCloseButtonClass}>
            <IconButton onClick={onCloseHandler} label={closeText} size="regular">
              <CloseIcon />
            </IconButton>
          </div>
        )}
      </div>
    );
  },
);

NotificationBox.toString = () => `.${ownClassName}`;

NotificationBox.displayName = 'NotificationBox';

export { notificationBoxBaseClass, notificationBoxVariantClass } from './NotificationBox.css';

type NotificationVariants = {
  emphasis?: ResponsiveValue<EmphasisValue>;
  isElevated?: boolean;
  variant?: VariantTypes;
};

type EmphasisSparky = Extract<NotificationVariants['emphasis'], object>;
export type VariantTypes = 'info' | 'success' | 'warning' | 'error' | 'chat';
